"use client";

import type { Column as TanstackColumn } from "@tanstack/react-table";
import { ChevronDown, ChevronRight as ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { useTableLabelFn } from "./labels-context";
import { getHeaderText } from "./smart-table-shared";

import { useSmartTableModelContext } from "./smart-table-model-context";

export function ColumnPicker<TData>() {
  const m = useSmartTableModelContext<TData>();
  const { columnGroups, enableColumnReorder, onVisibleColumnsChange, showColumns, columnSearch, setColumnSearch, t, actionColumnIds, table, Button } = m;
  return (
    <>
      {showColumns && (
        <div className="rounded-md border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t("table.columns.visible")}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onVisibleColumnsChange?.(null)}
            >
              {t("table.columns.showAll")}
            </Button>
          </div>
          <input
            type="text"
            value={columnSearch}
            onChange={(e) => setColumnSearch(e.target.value)}
            placeholder={t("table.columns.searchPlaceholder")}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            aria-label={t("table.columns.searchPlaceholder")}
          />
          {columnGroups.length > 0 ? (
            <div className="space-y-3">
              {columnGroups.map((group) => {
                const groupCols = table
                  .getAllLeafColumns()
                  .filter(
                    (c) =>
                      group.columnIds.includes(c.id) &&
                      !actionColumnIds.includes(c.id),
                  );
                return (
                  <ColumnGroupSection
                    key={group.id}
                    label={group.label}
                    columns={groupCols}
                    collapsedByDefault={group.collapsedByDefault ?? false}
                    searchQuery={columnSearch}
                  />
                );
              })}
              {(() => {
                const ungroupedCols = table
                  .getAllLeafColumns()
                  .filter(
                    (c) =>
                      !columnGroups.some((g) => g.columnIds.includes(c.id)) &&
                      !actionColumnIds.includes(c.id),
                  );
                if (ungroupedCols.length === 0) return null;
                return (
                  <ColumnGroupSection
                    label={t("otherColumns")}
                    columns={ungroupedCols}
                    collapsedByDefault={false}
                    searchQuery={columnSearch}
                  />
                );
              })()}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {table
                .getAllLeafColumns()
                .filter((c) => !actionColumnIds.includes(c.id))
                .filter((c) => {
                  if (!columnSearch) return true;
                  const label =
                    getHeaderText(c.columnDef.header) ??
                    (typeof c.columnDef.id === "string"
                      ? c.columnDef.id
                      : c.id);
                  return label
                    .toLowerCase()
                    .includes(columnSearch.toLowerCase());
                })
                .map((column) => {
                  const label =
                    getHeaderText(column.columnDef.header) ??
                    (typeof column.columnDef.id === "string"
                      ? column.columnDef.id
                      : column.id);
                  return (
                    <label
                      key={column.id}
                      className="inline-flex items-center gap-1 rounded-full bg-surface px-3 py-1 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                        aria-label={t("table.columns.aria.visibility", {
                          name: label,
                        })}
                      />
                      {label}
                    </label>
                  );
                })}
            </div>
          )}
          {enableColumnReorder && (
            <p className="text-xs text-text-secondary">
              {t("table.columns.dragHint")}
            </p>
          )}
        </div>
      )}

    </>  );
}

function ColumnGroupSection<TData>({
  label,
  columns,
  collapsedByDefault = false,
  searchQuery = "",
}: {
  label: string;
  columns: TanstackColumn<TData>[];
  collapsedByDefault?: boolean;
  searchQuery?: string;
}) {
  const t = useTableLabelFn();
  const [open, setOpen] = useState(!collapsedByDefault);
  const q = searchQuery.toLowerCase().trim();
  const filteredColumns = q
    ? columns.filter((column) => {
        const colLabel =
          getHeaderText(column.columnDef.header) ??
          (typeof column.columnDef.id === "string"
            ? column.columnDef.id
            : column.id);
        return colLabel.toLowerCase().includes(q);
      })
    : columns;
  const isSearching = q.length > 0;
  const effectivelyOpen = open || isSearching;
  if (filteredColumns.length === 0 && isSearching) return null;
  if (columns.length === 0) return null;
  return (
    <div className="rounded-md border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 p-2 text-left text-sm font-medium hover:bg-surface"
      >
        {effectivelyOpen ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRightIcon className="h-3 w-3" />
        )}
        {label} ({filteredColumns.length})
      </button>
      {effectivelyOpen && (
        <div className="flex flex-wrap gap-2 border-t p-2">
          {filteredColumns.map((column) => {
            const colLabel =
              getHeaderText(column.columnDef.header) ??
              (typeof column.columnDef.id === "string"
                ? column.columnDef.id
                : column.id);
            return (
              <label
                key={column.id}
                className="inline-flex items-center gap-1 rounded-full bg-surface px-3 py-1 text-xs"
              >
                <input
                  type="checkbox"
                  checked={column.getIsVisible()}
                  onChange={column.getToggleVisibilityHandler()}
                  aria-label={t("table.columns.aria.visibility", {
                    name: colLabel,
                  })}
                />
                {colLabel}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- SmartEditModal: auto-generated edit form from column definitions ---

